import React, { useState, useEffect } from 'react';
import { UsersIcon, MapIcon, TrophyIcon, CalendarIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { usePlanFeatures } from '../hooks/useStoreData';
import { DowngradeConfirmationModal } from '../components/DowngradeConfirmationModal';
import { DowngradeSuccessModal } from '../components/DowngradeSuccessModal';

interface DashboardData {
  overview: {
    totalUsers: number;
    totalRides: number;
    upcomingRides: number;
    totalParticipations: number;
    recentUsers: number;
    averageParticipantsPerRide: number;
  };
  popularRides: Array<{
    id: string;
    name: string;
    departureDate: string;
    participantCount: number;
  }>;
}

export const DashboardPage: React.FC = () => {
  const { user, backendUser, logout } = useAuth();
  const storeId = backendUser?.storeId || null;
  const { planFeatures } = usePlanFeatures(storeId);
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [storeInfo, setStoreInfo] = useState<any>(null);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [showDowngradeSuccess, setShowDowngradeSuccess] = useState(false);
  const [downgradeMessage, setDowngradeMessage] = useState('');

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!user) return;
      
      try {
        const response = await api.get('/admin/dashboard');
        setData(response.data);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
        // Only show error if user is authenticated
        if (user) {
          console.error('Dashboard data fetch failed for authenticated user');
        }
      } finally {
        setLoading(false);
      }
    };

    const fetchStoreInfo = async () => {
      if (!storeId) return;
      
      try {
        // Add cache-busting parameter to ensure fresh data
        const response = await api.get(`/stores/${storeId}`, {
          params: { _t: Date.now() }
        });
        setStoreInfo(response.data.store);
      } catch (error) {
        console.error('Failed to fetch store info:', error);
      }
    };

    if (user) {
      fetchDashboardData();
      if (storeId) {
        fetchStoreInfo();
      }
    } else {
      setLoading(false);
    }
  }, [user, storeId]);

  // Refresh store info when returning from payment success page
  useEffect(() => {
    if (!storeId) return;

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('payment') === 'success') {
      // Force refresh store info after payment with cache busting
      // Wait a bit for webhook to process, then refresh
      setTimeout(() => {
        api.get(`/stores/${storeId}`, { 
          params: { _t: Date.now() },
          headers: { 'Cache-Control': 'no-cache' } 
        }).then(response => {
          console.log('🔄 Refreshed store info after payment:', response.data.store);
          setStoreInfo(response.data.store);
          // Clean up URL parameter
          window.history.replaceState({}, '', '/dashboard');
        }).catch(err => console.error('Failed to refresh store info:', err));
      }, 3000); // Wait 3 seconds for webhook to process
    }
  }, [storeId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-gray-500">Loading dashboard...</div>
      </div>
    );
  }

  // Filter stats based on user role - only show "Total Users" for Super Admin
  const isSuperAdmin = backendUser?.role === 'SUPERADMIN';
  
  const allStats = [
    { 
      name: 'Total Users', 
      value: data?.overview.totalUsers.toString() || '0', 
      icon: UsersIcon, 
      color: 'bg-blue-500',
      showFor: ['SUPERADMIN'] as string[]
    },
    { 
      name: 'Total Rides', 
      value: data?.overview.totalRides.toString() || '0', 
      icon: MapIcon, 
      color: 'bg-green-500',
      showFor: ['ALL'] as string[]
    },
    { 
      name: 'Upcoming Rides', 
      value: data?.overview.upcomingRides.toString() || '0', 
      icon: TrophyIcon, 
      color: 'bg-purple-500',
      showFor: ['ALL'] as string[]
    },
    { 
      name: 'New This Month', 
      value: data?.overview.recentUsers.toString() || '0', 
      icon: CalendarIcon, 
      color: 'bg-orange-500',
      showFor: ['ALL'] as string[]
    },
  ];
  
  const stats = allStats.filter(stat => 
    stat.showFor.includes('ALL') || 
    (isSuperAdmin && stat.showFor.includes('SUPERADMIN'))
  );

  // Calculate trial status
  const getTrialStatus = () => {
    if (!storeInfo) return null;
    
    const trialEndsAt = storeInfo.trialEndsAt;
    const subscription = storeInfo.subscription;
    
    // Check if there's an active subscription (ONLY ACTIVE or TRIALING - PENDING does NOT grant access)
    if (subscription) {
      const activeStatuses = ['ACTIVE', 'TRIALING']; // PENDING subscriptions do NOT grant access - payment must complete
      if (activeStatuses.includes(subscription.status)) {
        return null; // Has active subscription, no trial message needed
      }
      // If subscription exists but is PENDING, still show upgrade button (payment not completed)
    }
    
    if (trialEndsAt) {
      const trialEnd = new Date(trialEndsAt);
      const now = new Date();
      const daysLeft = Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysLeft < 0) {
        return {
          type: 'expired',
          message: 'Trial Expired',
          action: 'Subscribe to Essential Plan',
          planId: 'ESSENTIAL'
        };
      }
      
      return {
        type: 'active',
        message: `Trial: ${daysLeft} days remaining`,
        action: daysLeft <= 7 ? 'Upgrade Now' : null,
        planId: 'ESSENTIAL'
      };
    }
    
    return {
      type: 'no-plan',
      message: 'No Active Plan',
      action: 'Subscribe to Essential Plan',
      planId: 'ESSENTIAL'
    };
  };

  const trialStatus = getTrialStatus();

  const handleUpgrade = async () => {
    if (!storeId || !trialStatus) return;
    
    try {
      const response = await api.post('/payments/create-checkout-session-for-store', {
        planId: trialStatus.planId
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error: any) {
      console.error('Failed to create checkout session:', error);
      alert(error.response?.data?.error || 'Failed to start upgrade process');
    }
  };

  const handleDowngrade = async () => {
    if (!storeId) return;
    
    try {
      const response = await api.post('/payments/downgrade-to-starter');
      
      // Close confirmation modal
      setShowDowngradeModal(false);
      
      // Set success message
      const message = storeInfo?.subscription 
        ? 'Your account has been downgraded to Starter plan.'
        : 'Starter plan activated.';
      
      setDowngradeMessage(message);
      setShowDowngradeSuccess(true);
    } catch (error: any) {
      console.error('Failed to downgrade:', error);
      alert(error.response?.data?.error || 'Failed to downgrade account');
    }
  };

  const handleLogoutAfterDowngrade = async () => {
    setShowDowngradeSuccess(false);
    await logout();
    window.location.href = '/store';
  };

  const handleChoosePayment = async () => {
    setShowDowngradeModal(false);
    
    // Determine which plan to subscribe to based on current plan
    const currentPlan = storeInfo?.subscription?.plan?.id || 'ESSENTIAL';
    const planId = currentPlan === 'ESSENTIAL' ? 'ESSENTIAL' : 'PROFESSIONAL';
    
    try {
      const response = await api.post('/payments/create-checkout-session-for-store', {
        planId
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error: any) {
      console.error('Failed to create checkout session:', error);
      alert(error.response?.data?.error || 'Failed to start payment process');
    }
  };

  // Check if user has an active subscription (can downgrade)
  const canDowngrade = storeInfo?.subscription?.status === 'ACTIVE' && 
                       storeInfo?.subscription?.plan?.id !== 'STARTER';

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">Welcome to RideConnect Admin Panel</p>
      </div>

      {/* Trial Status / Upgrade Banner */}
      {trialStatus && (backendUser?.role === 'STORE_ADMIN' || backendUser?.role === 'STORE_MANAGER') && (
        <div className={`mb-6 rounded-lg p-4 ${
          trialStatus.type === 'expired' 
            ? 'bg-red-50 border border-red-200' 
            : trialStatus.type === 'active'
            ? 'bg-yellow-50 border border-yellow-200'
            : 'bg-blue-50 border border-blue-200'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <ExclamationTriangleIcon className={`w-5 h-5 mr-3 ${
                trialStatus.type === 'expired' ? 'text-red-600' : 'text-yellow-600'
              }`} />
              <div>
                <p className={`font-semibold ${
                  trialStatus.type === 'expired' ? 'text-red-900' : 'text-yellow-900'
                }`}>
                  {trialStatus.message}
                </p>
                {trialStatus.type === 'active' && (
                  <p className="text-sm text-yellow-700 mt-1">
                    Your trial will expire soon. Upgrade to continue using all features.
                  </p>
                )}
                {trialStatus.type === 'expired' && (
                  <p className="text-sm text-red-700 mt-1">
                    Your trial has expired. Subscribe to continue using RideConnect.
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-3">
              {trialStatus.action && (
                <button
                  onClick={handleUpgrade}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  {trialStatus.action}
                </button>
              )}
              {trialStatus.type === 'expired' && (
                <button
                  onClick={() => setShowDowngradeModal(true)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Downgrade to Starter
                </button>
              )}
            </div>
          </div>
        </div>
      )}


      {/* Downgrade Confirmation Modal */}
      {/* Show modal if: active subscription (can downgrade) OR trial expired (can activate Starter) */}
      {(canDowngrade || (trialStatus?.type === 'expired' && !storeInfo?.subscription)) && (
        <DowngradeConfirmationModal
          isOpen={showDowngradeModal}
          onClose={() => setShowDowngradeModal(false)}
          onConfirmDowngrade={handleDowngrade}
          onChoosePayment={handleChoosePayment}
          currentPlan={storeInfo?.subscription?.plan?.id || 'ESSENTIAL'}
        />
      )}

      {/* Downgrade Success Modal */}
      <DowngradeSuccessModal
        isOpen={showDowngradeSuccess}
        onClose={() => setShowDowngradeSuccess(false)}
        onLogout={handleLogoutAfterDowngrade}
        message={downgradeMessage}
      />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat) => (
          <div key={stat.name} className="card">
            <div className="flex items-center">
              <div className={`p-3 rounded-lg ${stat.color}`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Popular Rides</h3>
          {data?.popularRides && data.popularRides.length > 0 ? (
            <div className="space-y-3">
              {data.popularRides.slice(0, 5).map((ride) => (
                <div key={ride.id} className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-900 font-medium">{ride.name}</p>
                    <p className="text-gray-500 text-sm">
                      {new Date(ride.departureDate).toLocaleDateString()}
                    </p>
                  </div>
                  <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm">
                    {ride.participantCount} riders
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-gray-500 text-center py-8">
              No rides yet. Create your first ride to get started!
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
